/**
 * Events menu: clickable list of events/custom events to go inside mp-drop-menu
 */
import sum from 'lodash/sum';

import {assertUnreachable} from 'common/util/typescript';
import {ContextPanelData} from 'common/widgets/context-panel/types';
import {LocalStorageKey, ProjectFeatureFlag, ResourceType} from 'common/report/constants';
import {getIconForEvent} from 'common/report/util/events';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {debounce} from 'common/util/lodash-wrapped';
import {NUM_RECENT_ITEMS, TRACKING_EVENTS} from 'common/query-builder/query-block/constants';
import {SORT_OPTION_ITEMS} from 'common/query-builder/query-block/properties-menu/constants';
import {
  ExpanderItemClickEvent,
  ExpansionToggleItemClickEvent,
  ExpansionToggleLabelMode,
  ItemClickEvent,
  ItemFocusEvent,
  Section,
  SectionItem,
  SelectedTheme,
} from 'common/components/items-menu/types';
import {promptCustomEvents} from 'common/widgets/custom-event-modal/utils';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {DummyStorageManager} from 'common/contexts/storage-manager/dummy';
import {captureWorkflowTiming} from 'common/util/workflow-timing';
import {featureSamplingUtils} from 'common/report/util/feature-sampling';
import {TagInputChangeAction, TagInputChangeEvent} from 'common/components/tag-input/types';
import {TagItem} from 'common/components/tag-input/tag-item';
import {Visibility, VisibilityChangeEvent} from 'common/components/base/stackable';
import {UserLocalStorageHelper} from 'common/report/user-local-storage';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';

import {ChildMenu} from '../child-menu';
import {EventsController, getCustomEventMenuItem} from '../contexts/events-controller';
import {
  FilterMenuChangeEvent,
  FilterValue,
  SortBy,
  SortMenuChangeEvent,
  SortSectionItem,
} from '../properties-menu/types';
import {canUseImprovedQbMenus, getOpenedMenuComponent, OpenedMenuComponent} from '../utils/util';
import {template as noTagsTemplate} from './no-tags';
import {template} from './template';
import {
  ActionType,
  Event,
  EventItem,
  EventMenuItem,
  EventTagMenuItem,
  ModalType,
  SelectEventItem,
  SidebarCategory,
  SidebarItem,
  SidebarItemType,
} from './types';
import {filterToBookmarkableEventItem, getEventSelectedLabel} from './util';
import {renderEventItemContextPanel} from './context-panel';
import {AllEventsFilter} from './constants';

const USER_STORAGE_KEY = `default`;

interface EventsMenuState {
  eventData: EventItem[];
  isMenuLoading: boolean;
  isMenuOpen: boolean;
  permissions: Set<string>;
  includeHidden: boolean;
  sortByValue: SortBy;
  searchFilter: string;
  tooltipData: ContextPanelData;
  selectedTagFilter: FilterValue;

  /** state specific to improved QB menu */
  searchInputEl: HTMLElement;
  isSearchInputFocused: boolean;
  isSearchTagsMenuOpen: boolean;
  isSidebarTagSectionExpanded: boolean;
  selectedTagSet: Set<string>;
}

type PersistedEventsMenuState = Pick<EventsMenuState, 'isSidebarTagSectionExpanded'>;

export class EventsMenu extends ChildMenu<EventsMenuState> {
  static contextPanelRenderer = renderEventItemContextPanel;
  private _openedAt: number = null;
  private _openCount = 0;
  private _numTotalItemsOnPrevRender: number = null;
  private _userLocalStorageHelper: UserLocalStorageHelper<PersistedEventsMenuState>;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'other-options': {
        type: `json`,
        default: [],
        description: `list of items to show in the other section`,
      },
    };
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    // set open before any other code executes.
    this.setOpenedAt();

    super.attributeChangedCallback(attr, oldValue, newValue);

    if (attr === `open`) {
      this.update({
        includeHidden: false,
        sortByValue: this.config.defaultState.sortByValue,
        selectedTagSet: new Set(),
        searchFilter: ``,
      });

      if (this.attr(`open`)) {
        this.trackEvent(TRACKING_EVENTS.OPEN_EVENTS_DROPDOWN);
      } else {
        // when events-menu closes, all context menu also closes.
        this.dispatchEvent(new CustomEvent(`closeContextMenu`, {detail: {isOpen: false}, bubbles: true}));
      }
    }
  }

  get userLocalStorageHelper(): UserLocalStorageHelper<PersistedEventsMenuState> {
    if (!this._userLocalStorageHelper) {
      this._userLocalStorageHelper = new UserLocalStorageHelper({
        context: this.getContext(`localStorageManager`),
        namespace: LocalStorageKey.EventsMenu,
      });
    }
    return this._userLocalStorageHelper;
  }

  setPersistedState(): void {
    const {isSidebarTagSectionExpanded} = this.state;
    this.userLocalStorageHelper.setItem({
      userId: userCookieUtils.getUserIdentityFromCookie()?.id,
      key: USER_STORAGE_KEY,
      value: {isSidebarTagSectionExpanded},
    });
  }

  connectedCallback() {
    this.setOpenedAt();

    const persistedState = this.userLocalStorageHelper.getItem({
      key: USER_STORAGE_KEY,
      userId: userCookieUtils.getUserIdentityFromCookie()?.id,
    });
    if (persistedState) {
      this.update(persistedState);
    }

    super.connectedCallback();
  }

  /**
   * See comment for analogous functionality in <mp-properties-menu>
   */
  setOpenedAt() {
    // setOpenedAt may be called before this.attr managed state is available.
    if (this.isAttributeEnabled(`open`)) {
      if (!this._openedAt) {
        this._openedAt = new Date().getTime();
        this._openCount += 1;

        let openedComponent: OpenedMenuComponent;
        captureWorkflowTiming(this, {
          name: `Events Menu`,
          key: `events-menu`,
          isComplete: (ev) => {
            openedComponent = getOpenedMenuComponent(ev, this);
            return Boolean(openedComponent);
          },
          additionalProps: () => ({
            openCount: this._openCount,
            openedComponent,
          }),
        });
        captureWorkflowTiming(this, {
          name: `Events Menu Loaded`,
          key: `events-menu-loaded`,
          isComplete: (ev) => getOpenedMenuComponent(ev, this) === OpenedMenuComponent.ItemsMenu,
          additionalProps: () => ({
            numTotalItems: this._numTotalItemsOnPrevRender,
            openCount: this._openCount,
          }),
        });
      }
    } else {
      this._openedAt = null;
    }
  }

  get config() {
    return {
      template,

      contexts: [`eventsController` as const, `localStorageManager` as const, `currentProject` as const],
      defaultContexts: {
        eventsController: new EventsController(),
        localStorageManager: new DummyStorageManager(),
      },

      defaultState: {
        eventData: [],
        tooltipData: null,
        includeHidden: false,
        sortByValue: SortBy.Relevance,
        tags: [],
        selectedTagFilter: AllEventsFilter.value,
        searchFilter: ``,
        searchInputEl: null,
        isSearchInputFocused: false,
        isSearchTagsMenuOpen: false,
        isSidebarTagSectionExpanded: false,
        selectedTagSet: new Set(),
      },

      helpers: {
        ...super.config.helpers,
        getMenuSections: () => {
          const sections = this.getSections();
          this._numTotalItemsOnPrevRender = sum(sections.map((section) => section.items.length));
          return sections;
        },
        isLoading: () => this.getContext(`eventsController`).loading,
        getSortingOptionSectionItems: (): SortSectionItem[] => {
          return SORT_OPTION_ITEMS.map((option) => ({
            ...option,
            isSelected: option.value === this.state.sortByValue,
          }));
        },
        handleTagFilterChange: ({detail}: FilterMenuChangeEvent) => {
          const selectedTagFilter = detail.item.value;
          this.update({selectedTagFilter});
          this.trackEvent(TRACKING_EVENTS.CHANGED_FILTER, {filter: selectedTagFilter});
        },
        handleSortChange: ({detail}: SortMenuChangeEvent) => {
          const sortByValue = detail.item.value;
          this.update({sortByValue});
          this.trackEvent(TRACKING_EVENTS.CHANGED_SORTING, {'sorting type': sortByValue});
        },
        handleSearchInput: (searchInput) => this.debouncedSearchInput(searchInput),

        canCreateItems: (): boolean => {
          return (
            !this.attr(`exclude-custom-events`) &&
            !this.attr(`hide-edit-controls`) &&
            this.getContext(`eventsController`).canWriteCustomEvents()
          );
        },

        getSearchPlaceholder: () => {
          return `Search...`;
        },

        /** appends a manual event entry item */
        sectionsFromInput: (searchFilter: string, sections: Section[]): Array<Section<EventMenuItem>> => {
          return [
            ...(this.canUseImprovedQbMenus() && sections.every((section) => section.items.length === 0)
              ? [
                  {
                    label: `No results found.`,
                    alwaysShowLabel: true,
                    items: [],
                  },
                ]
              : []),
            {
              items: [
                {
                  label: `"${searchFilter}"`,
                  value: {
                    value: searchFilter,
                    label: searchFilter,
                  },
                  itemFromInput: true,
                },
                ...(this.canUseImprovedQbMenus()
                  ? [
                      {
                        label: `Refresh`,
                        icon: `refresh`,
                        isSpecialAction: true,
                        excludeFromInputMatch: true,
                        value: {
                          action: ActionType.Refresh as const,
                        },
                      },
                    ]
                  : []),
              ],
            },
          ];
        },

        // This is for the Create Custom button at the bottom of the menu
        triggerCreateCustomEventModal: () => this.triggerModal(ModalType.CreateCustomEvent),

        shouldShowRefreshButton: () => {
          const currentProject = this.getContext(`currentProject`).get().value;

          if (Boolean(currentProject) === false) {
            return false;
          }

          return (
            currentProject?.hasFeatureFlag(ProjectFeatureFlag.EventsMenuRefresh) ||
            featureSamplingUtils.determineFeatureEligibility(
              currentProject?.id.toString(),
              ProjectFeatureFlag.EventsMenuRefresh,
              20,
            )
          );
        },
        handleRefreshButtonClick: () => {
          this.refreshEvents();
        },
      },
    };
  }

  getItemUniqueKey(item: EventItem) {
    return item.value;
  }

  handleCustomEventDelete(deletedId: number) {
    if (deletedId && this.attr(`selected`)?.id === deletedId) {
      // if deleting the selected custom event we delete the entire block
      this.dispatchEvent(new CustomEvent(`delete`));
    }
  }

  handleCloseMenu() {
    this.updateContextPanel.cancel();
  }

  handleExpandItemClick({detail: {section}}: ExpanderItemClickEvent) {
    if (section.label === `Hidden Events`) {
      this.trackEvent(TRACKING_EVENTS.LEXICON_SHOW_HIDDEN);
    }
  }

  handleItemFocus({detail}: ItemFocusEvent<EventItem>) {
    this.updateContextPanel(detail.item.value);
  }

  async triggerModal(modalType: ModalType): Promise<void> {
    if (modalType === ModalType.CreateCustomEvent) {
      this.onModalOpen();
      const {appliedValue} = await promptCustomEvents({
        open: true,
        'all-data-groups': this.attr(`all-data-groups`),
        'project-has-people': this.attr(`project-has-people`),
        'project-id': getProjectId(),
        'workspace-id': getWorkspaceId(),
        'track-name': this.attr(`track-name`),
      });

      if (appliedValue) {
        const eventItem = getCustomEventMenuItem(appliedValue).value;
        this.selectItem(eventItem);
      }
      this.onModalClose();
    }
  }

  selectItem(
    rawItem: EventItem,
    {searchFilter = ``, labelMatch = {}, subLabelMatch = {}, customMatch = {}, topScrollPosition = 0} = {},
  ) {
    const item = filterToBookmarkableEventItem(rawItem);

    this.addRecentItem(ResourceType.Event, item);

    const trackingProps = this.trackingProps(
      item,
      searchFilter,
      labelMatch,
      subLabelMatch,
      customMatch,
      topScrollPosition,
    );
    this.trackSelection(trackingProps);
    this.dispatchEvent(
      new CustomEvent<SelectEventItem>(`select`, {detail: {item}}),
    );
  }

  handleClickItem({detail}: ItemClickEvent<EventMenuItem>) {
    const {item} = detail;
    switch (item.value.action) {
      case ActionType.TriggerModal:
        this.triggerModal(item.value.modalType);
        break;
      case ActionType.Refresh:
        this.refreshEvents();
        break;
      case ActionType.Select:
      case undefined:
        this.selectItem(item.value, detail);
        break;
      default:
        assertUnreachable(item.value);
    }
  }

  getTooltipData(item: EventItem): ContextPanelData {
    const tooltipData = this.getContext(`eventsController`).getContextPanelData(item, {
      allowEdit: !this.attr(`readonly`) && !this.attr(`hide-edit-controls`),
    });

    return tooltipData;
  }

  private handleSearchInput(searchFilter) {
    const isStartingSearch = Boolean(searchFilter && !this.state.searchFilter);
    const isClearingSearch = Boolean(this.state.searchFilter && !searchFilter);
    this.update({searchFilter});
    if (isStartingSearch) {
      this.trackEvent(TRACKING_EVENTS.SEARCH_STARTED);
    } else if (isClearingSearch) {
      this.trackEvent(TRACKING_EVENTS.SEARCH_CLEARED);
    }
  }

  private debouncedSearchInput = debounce((searchInput) => this.handleSearchInput(searchInput), 200);

  private updateContextPanel = debounce(
    (focusedItem) => {
      this.dispatchEvent(new CustomEvent(`openContextMenu`, {detail: {isOpen: true}, bubbles: true}));
      this.update({tooltipData: this.getTooltipData(focusedItem)});
    },
    250,
    {leading: true},
  );

  getSelectedHoverTooltipData(): ContextPanelData {
    const selected = this.attr(`selected`);
    return selected && selected.value ? this.getTooltipData(selected) : null;
  }

  getSelectedTrackingProps(): Obj {
    const selected = this.attr(`selected`);
    if (!selected) {
      return null;
    }

    const props = {'Entity type': `event`};
    if (selected.custom) {
      props[`Is custom`] = true;
    }
    return props;
  }

  getPersistenceKey() {
    return `events-menu`;
  }

  getSections() {
    let tagFilters: string[];
    if (this.canUseImprovedQbMenus()) {
      tagFilters = Array.from(this.state.selectedTagSet);
    } else {
      tagFilters =
        this.state.selectedTagFilter.value !== AllEventsFilter.value.value ? [this.state.selectedTagFilter.value] : [];
    }
    const hasTagFilter = Boolean(tagFilters.length);

    return [
      /* @todo qb-menus -- re-enable properties menu tests in storybook */
      ...(this.canUseImprovedQbMenus() ? [this.getRecentsSection()] : []),
      ...this.getContext(`eventsController`).getSections({
        dataGroupId: this.attr(`data-group-id`),
        excludeAutotrackEvents: this.attr(`exclude-autotrack-events`),
        excludeCustomEvents: this.attr(`exclude-custom-events`),
        mainSectionShowLimit: 0,
        searchFilter: this.state.searchFilter,
        selected: this.attr(`selected`),
        showAllEvents: this.attr(`show-all-events`) && !hasTagFilter,
        showAnyEvent: this.attr(`show-any-event`) && !hasTagFilter,
        showTopEvents: this.attr(`show-top-events`) && !hasTagFilter,
        showSessionEvent: this.attr(`show-session-event`),
        showSpecialDatasetProperty: this.attr(`show-special-dataset-property`),
        sortByValue: this.canSortItems() ? this.state.sortByValue : SortBy.Relevance,
        recentlyUsedEvents: this.getRecentItems(ResourceType.Event),
        tagFilters,
        otherOptions: this.attr(`other-options`),
        canUseImprovedQbMenus: this.canUseImprovedQbMenus(),
      }),
    ].filter(Boolean);
  }

  getTagFilterMenuItems() {
    const tags = this.getTagsSection();
    const currentSelection = this.state.selectedTagFilter.value;
    const allEvents =
      currentSelection === AllEventsFilter.value.value ? {...AllEventsFilter, isSelected: true} : AllEventsFilter;

    const sections = [
      {label: `Filter by`, items: [allEvents]},
      {
        label: `Filter by Tag`,
        items: tags?.items?.map((tag) => ({
          ...tag,
          hasCaret: false,
          isSelected: this.state.selectedTagFilter.value === tag.value.value,
        })),
      },
    ];

    return sections;
  }

  getRecentsSection(): Section<EventItem> {
    const recentsSection = this.getContext(`eventsController`).getRecentsSection(
      this.getRecentItems(ResourceType.Event, NUM_RECENT_ITEMS) as EventItem[],
    );
    return {
      ...recentsSection,
      items: recentsSection.items.filter((eventItem) => {
        return Boolean(
          Array.from(this.state.selectedTagSet).every((tagFilter) => {
            return Boolean(eventItem.value.tags?.includes(tagFilter));
          }),
        );
      }),
    };
  }

  selectedLeftIcon() {
    return getIconForEvent(this.state.selected);
  }

  selectedLabel() {
    return getEventSelectedLabel(this.state.selected as Event, {
      includeNameOnRename: true,
      labelPrefix: this.state.selected?.labelPrefix,
    });
  }

  trackingProps(selected, searchFilter, labelMatch, subLabelMatch, customMatch, topscrollPosition) {
    const extraData = this.getTooltipData(selected);
    const extraEventTrackingProps = {
      'num of images': extraData.imageInfo?.length,
    };

    return {
      eventName: `[mp-query-builder] Select event`,
      props: {
        'Event name': selected.label,
        'top scroll': topscrollPosition,
        'is custom event': !!selected.custom,
        'is autotrack event': !!selected.isCollectEverything,
        'is hidden entity': !!selected.hidden,
        'is recently viewed': !!selected.isRecentlyUsed,
        'Search - filter': searchFilter,
        'Search - label match': labelMatch,
        'Search - sublabel match': subLabelMatch,
        'Search - custom match': customMatch,
        'Metadata Search Enabled': true,
        'Elapsed time (ms) since menu opened': new Date().getTime() - this._openedAt,
        ...extraEventTrackingProps,
      },
    };
  }

  public canSortItems(): boolean {
    return !this.canUseImprovedQbMenus();
  }

  refreshEvents(): void {
    this.getContext(`eventsController`).refreshData();
    this.trackEvent(TRACKING_EVENTS.REFRESHED_EVENTS);
  }

  //#region improved QB menu specific code

  canUseImprovedQbMenus(): boolean {
    return canUseImprovedQbMenus(this.getContext(`currentProject`).get().value);
  }

  getSearchSelectedTagItems(): TagItem[] {
    return this.getTagsSection()
      .items.filter((tag) => this.state.selectedTagSet.has(tag.value.value))
      .map((tag) => {
        return {
          value: tag.value.value,
          label: tag.label,
        };
      });
  }

  handleSearchTagMenuChange(ev: VisibilityChangeEvent): void {
    this.update({isSearchTagsMenuOpen: ev.detail.state === Visibility.Open});
  }

  deleteTag(tag: string): void {
    const updatedSelectedTagSet = new Set(this.state.selectedTagSet);
    updatedSelectedTagSet.delete(tag);
    this.update({selectedTagSet: updatedSelectedTagSet});
  }

  addTag(tag: string): void {
    const updatedSelectedTagSet = new Set(this.state.selectedTagSet);
    updatedSelectedTagSet.add(tag);
    this.update({selectedTagSet: updatedSelectedTagSet});
  }

  handleSearchInputInsert({elm}: {elm: Node}) {
    this.update({searchInputEl: elm as HTMLElement});
  }

  getSearchTagMenuSections(): Array<Section<TagItem>> {
    const tagsSection = this.getTagsSection();
    if (!tagsSection.items.length) {
      return [];
    }

    return [
      {
        label: `Select Tag`,
        alwaysShowLabel: true,
        items: tagsSection.items.map((tag) => {
          return {
            label: tag.label,
            value: {
              label: tag.label,
              value: tag.value.value,
            },
          };
        }),
      },
    ];
  }

  handleSearchInputChange(ev: TagInputChangeEvent): void {
    const {detail} = ev;
    if (detail.action === TagInputChangeAction.Text) {
      this.debouncedSearchInput(detail.value);
    } else if (detail.action === TagInputChangeAction.RemoveTag) {
      this.deleteTag(detail.tag.value);
    } else if (detail.action === TagInputChangeAction.AddTag) {
      this.addTag(detail.tag.value);
    }
  }

  handleSearchInputClear(): void {
    this.update({
      searchFilter: ``,
      selectedTagSet: new Set(),
    });
  }

  handleSearchInputFocus(): void {
    this.update({isSearchInputFocused: true});
  }

  handleSearchInputBlur(): void {
    this.update({isSearchInputFocused: false});
  }

  getTagsSection(): Section<EventTagMenuItem> {
    return this.getContext(`eventsController`).getEventTagsSection({
      excludeCustomEvents: false,
      showLimit: null,
      canUseImprovedQbMenus: this.canUseImprovedQbMenus(),
    });
  }

  getSidebarSections(): Array<Section<SidebarItem>> {
    const tagsSection = this.getTagsSection();

    const sidebarTagSection: Section<SidebarItem> = {
      icon: `tag`,
      label: `Tags`,
      selectedTheme: SelectedTheme.Checkmark,
      expansionToggle: {
        isExpanded: this.state.isSidebarTagSectionExpanded,
        labelMode: ExpansionToggleLabelMode.Unchanged,
        alwaysShow: true,
      },
      items: tagsSection.items.map(
        (tag): SectionItem<SidebarItem> => {
          return {
            label: tag.label,
            isSelected: this.state.selectedTagSet.has(tag.value.value),
            value: {
              type: SidebarItemType.EventTag,
              tag: tag.value.value,
            },
          };
        },
      ),
      emptySectionRenderer: noTagsTemplate,
    };

    return [
      {
        selectedTheme: SelectedTheme.Focus,
        items: [
          {
            label: `All`,
            icon: `event`,
            isSelected: true,
            value: {
              type: SidebarItemType.Category,
              category: SidebarCategory.All,
            },
          },
        ],
      },
      sidebarTagSection,
    ];
  }

  handleClickSidebarItem({detail}: ItemClickEvent<SidebarItem>): void {
    const itemValue = detail.item.value;
    if (itemValue.type === SidebarItemType.EventTag) {
      const {tag} = itemValue;
      if (this.state.selectedTagSet.has(tag)) {
        this.deleteTag(tag);
      } else {
        this.addTag(tag);
      }
    }
    this.state.searchInputEl?.focus();
  }

  handleClickSidebarExpansionToggleItem({detail}: ExpansionToggleItemClickEvent): void {
    this.update({isSidebarTagSectionExpanded: detail.isExpanded});
    this.setPersistedState();
  }

  //#endregion
}

export const EventsMenuEl = defineAndMakeJsxFactory(`mp-events-menu`, EventsMenu);
export const EventsMenuReact = panel2react(`mp-events-menu`, EventsMenu);
